(function() {
  const INTENT_API_BASE_FALLBACK =
    "https://ek1yr83d8a.execute-api.ap-northeast-1.amazonaws.com/intent";

  class PurchaseIntent extends HTMLElement {
    constructor() {
      super();

      this.apiBase = this.dataset.apiBase || INTENT_API_BASE_FALLBACK;
      this.token = new URLSearchParams(window.location.search).get("token");
      this.elements = {
        loading: this.querySelector("[data-intent-loading]"),
        error: this.querySelector("[data-intent-error]"),
        intro: this.querySelector("[data-intent-intro]"),
        content: this.querySelector("[data-intent-content]"),
        complete: this.querySelector("[data-intent-complete]"),
        orderName: this.querySelector("[data-intent-order-name]"),
        deadlineItem: this.querySelector("[data-intent-deadline-item]"),
        deadline: this.querySelector("[data-intent-deadline]"),
        items: this.querySelector("[data-intent-items]"),
        shipping: this.querySelector("[data-intent-shipping]"),
        totals: this.querySelectorAll("[data-intent-total]"),
        agreement: this.querySelector("[data-intent-agreement]"),
        agreementCheck: this.querySelector("[data-intent-agreement-check]"),
        actions: this.querySelector("[data-intent-actions]"),
        agree: this.querySelector("[data-intent-agree]"),
        confirmButton: this.querySelector("[data-intent-confirm]"),
        declineButton: this.querySelector("[data-intent-decline]"),
        completeTitle: this.querySelector("[data-intent-complete-title]"),
        completeMessage: this.querySelector("[data-intent-complete-message]")
      };
    }

    connectedCallback() {
      this.bindEvents();
      this.load();
    }

    bindEvents() {
      this.elements.agree?.addEventListener("change", () => {
        this.elements.confirmButton.disabled = !this.elements.agree.checked;
      });

      this.elements.confirmButton?.addEventListener("click", () => {
        this.submit("confirm");
      });

      this.elements.declineButton?.addEventListener("click", () => {
        this.submit("decline");
      });
    }

    async load() {
      if (!this.token) {
        this.showError("確認URLが正しくありません。メールに記載されたURLから再度アクセスしてください。");
        return;
      }

      this.setLoading(true);

      try {
        const response = await fetch(this.buildUrl(), {
          method: "GET",
          headers: { Accept: "application/json" }
        });

        const body = await this.parseJson(response);

        if (!response.ok) {
          throw new Error(this.errorMessage(body, "ご購入の意思確認情報を取得できませんでした。"));
        }

        this.render(body);
      } catch (error) {
        this.showError(error.message || "ご購入の意思確認情報を取得できませんでした。");
      } finally {
        this.setLoading(false);
      }
    }

    async submit(intent) {
      const isConfirm = intent === "confirm";

      if (isConfirm && !this.elements.agree?.checked) {
        this.showError("契約同意文をご確認のうえ、同意にチェックを入れてください。");
        return;
      }

      this.setSubmitting(true);
      this.hideError();

      try {
        const response = await fetch(this.buildUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify(
            isConfirm ? { intent: "confirm", agreed: true } : { intent: "decline" }
          )
        });

        const body = await this.parseJson(response);

        if (!response.ok) {
          throw new Error(this.errorMessage(body, "送信できませんでした。時間をおいて再度お試しください。"));
        }

        this.showComplete(isConfirm);
      } catch (error) {
        this.showError(error.message || "送信できませんでした。時間をおいて再度お試しください。");
      } finally {
        this.setSubmitting(false);
      }
    }

    render(rawData) {
      const data = rawData?.draftOrder || rawData?.draft_order || rawData?.order || rawData || {};
      const lineItems = this.normalizeLineItems(
        data.lineItems || data.line_items || data.items || rawData?.lineItems || rawData?.items
      );
      const intentStatus = this.normalizeIntentStatus(data, rawData);

      this.elements.orderName.textContent =
        data.name || data.orderName || data.order_name || data.number || data.id || "-";
      this.elements.deadline.textContent = this.formatDate(
        data.reserveInventoryUntil || data.reserve_inventory_until || data.expiresAt || data.expires_at || data.deadline
      );
      this.elements.shipping.textContent = this.formatMoney(
        data.shippingPrice || data.shipping_price || data.shipping || data.shippingLine?.price || data.shipping_line?.price
      );
      this.setText(
        this.elements.totals,
        this.formatMoney(data.totalPrice || data.total_price || data.total || data.totalAmount || data.total_amount)
      );

      this.elements.items.innerHTML = "";

      if (lineItems.length === 0) {
        const row = document.createElement("tr");
        row.innerHTML = '<td colspan="4" class="purchase-intent__empty">商品情報を取得できませんでした。</td>';
        this.elements.items.appendChild(row);
      } else {
        lineItems.forEach((item) => this.elements.items.appendChild(this.createItemRow(item)));
      }

      this.elements.content.hidden = false;
      this.applyIntentStatus(intentStatus);
    }

    createItemRow(item) {
      const row = document.createElement("tr");
      const title = item.title || item.name || item.productTitle || item.product_title || "-";
      const quantity = item.quantity || item.currentQuantity || item.current_quantity || 1;
      const price = this.formatMoney(
        item.price || item.originalUnitPrice || item.original_unit_price || item.totalPrice || item.total_price
      );

      row.innerHTML = `
        <td>${this.escapeHtml(title)}</td>
        <td class="text-right">${this.escapeHtml(String(quantity))}</td>
        <td class="text-right">${this.escapeHtml(price)}</td>
        <td class="text-right">${this.escapeHtml(
          this.formatMoney(item.linePrice || item.line_price || item.total || item.totalPrice || item.total_price)
        )}</td>
      `;
      return row;
    }

    normalizeLineItems(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (Array.isArray(value.nodes)) return value.nodes;
      if (Array.isArray(value.edges)) return value.edges.map((edge) => edge.node || edge);
      return [];
    }

    normalizeIntentStatus(data, rawData) {
      const status =
        data.intentStatus ||
        data.intent_status ||
        data.intent ||
        data.status ||
        rawData?.intentStatus ||
        rawData?.intent_status ||
        rawData?.intent ||
        rawData?.status;
      const tags = this.normalizeTags(data.tags || rawData?.tags);
      const normalized = String(status || "").toLowerCase();

      if (
        normalized === "confirmed" ||
        normalized === "confirm" ||
        normalized === "intent:confirmed" ||
        tags.includes("intent:confirmed")
      ) {
        return "confirmed";
      }

      if (
        normalized === "declined" ||
        normalized === "decline" ||
        normalized === "intent:declined" ||
        normalized === "canceled" ||
        normalized === "cancelled" ||
        tags.includes("intent:declined")
      ) {
        return "declined";
      }

      return "pending";
    }

    normalizeTags(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value.map((tag) => String(tag).toLowerCase());
      if (typeof value === "string") {
        return value.split(",").map((tag) => tag.trim().toLowerCase());
      }
      return [];
    }

    buildUrl() {
      const url = new URL(this.apiBase, window.location.origin);
      url.searchParams.set("token", this.token);
      return url.toString();
    }

    async parseJson(response) {
      const text = await response.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch (_) {
        return null;
      }
    }

    errorMessage(body, fallback) {
      if (Array.isArray(body?.errors) && body.errors.length) {
        return body.errors.join("\n");
      }

      return body?.message || fallback;
    }

    applyIntentStatus(intentStatus) {
      const isPending = intentStatus === "pending";
      this.dataset.intentStatus = intentStatus;

      if (this.elements.agreement) this.elements.agreement.hidden = !isPending;
      if (this.elements.agreementCheck) this.elements.agreementCheck.hidden = !isPending;
      if (this.elements.actions) this.elements.actions.hidden = !isPending;
      if (this.elements.deadlineItem) this.elements.deadlineItem.hidden = !isPending;

      if (isPending) {
        this.elements.complete.hidden = true;
        this.elements.confirmButton.disabled = !this.elements.agree?.checked;
        this.elements.declineButton.disabled = false;
        return;
      }

      this.elements.confirmButton.disabled = true;
      this.elements.declineButton.disabled = true;

      switch (intentStatus) {
        case "declined":
          this.setIntroHtml("<p>回答済みのお見積もり内容をご確認いただけます。</p>");
          this.showStatusMessage(
            "ご購入の見送りを受け付け済みです",
            "ご回答ありがとうございました。またのご利用をお待ちしております。"
          );
          break;
        case "confirmed":
          this.setIntroHtml("<p>回答済みのお見積もり内容をご確認いただけます。</p>");
          this.showStatusMessage(
            "ご購入の意思を受け付け済みです",
            "お支払い方法につきましては、決済用のご案内メールをご確認ください。"
          );
          break;
        default:
          this.setIntroHtml("<p>この確認URLの現在の状態をご確認ください。</p>");
          this.showStatusMessage(
            "この確認URLは現在操作できません",
            "確認状況については、お問い合わせください。"
          );
          break;
      }
    }

    formatDate(value) {
      if (!value) return "-";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);

      return new Intl.DateTimeFormat("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      }).format(date);
    }

    formatMoney(value) {
      if (value === null || typeof value === "undefined" || value === "") return "-";

      if (typeof value === "object") {
        const amount =
          value.amount ||
          value.value ||
          value.shopMoney?.amount ||
          value.shop_money?.amount ||
          value.presentmentMoney?.amount ||
          value.presentment_money?.amount;
        const currency =
          value.currencyCode ||
          value.currency_code ||
          value.shopMoney?.currencyCode ||
          value.shop_money?.currency_code ||
          "JPY";
        return this.formatMoneyValue(amount, currency);
      }

      if (typeof value === "string" && /[^\d.-]/.test(value)) return value;

      return this.formatMoneyValue(value, "JPY");
    }

    formatMoneyValue(amount, currency) {
      const number = Number(amount);
      if (Number.isNaN(number)) return String(amount || "-");

      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency,
        maximumFractionDigits: currency === "JPY" ? 0 : 2
      }).format(number);
    }

    setLoading(isLoading) {
      this.elements.loading.hidden = !isLoading;
    }

    setSubmitting(isSubmitting) {
      this.elements.confirmButton.disabled = isSubmitting || !this.elements.agree?.checked;
      this.elements.declineButton.disabled = isSubmitting;
    }

    showError(message) {
      this.elements.error.textContent = message;
      this.elements.error.hidden = false;
    }

    hideError() {
      this.elements.error.hidden = true;
    }

    showComplete(isConfirm) {
      this.dataset.intentStatus = isConfirm ? "confirmed" : "declined";
      if (this.elements.deadlineItem) this.elements.deadlineItem.hidden = true;
      if (this.elements.agreement) this.elements.agreement.hidden = true;
      if (this.elements.agreementCheck) this.elements.agreementCheck.hidden = true;
      if (this.elements.actions) this.elements.actions.hidden = true;
      this.elements.completeTitle.textContent = isConfirm
        ? "ご購入の意思を受け付けました"
        : "ご購入の見送りを受け付けました";
      this.elements.completeMessage.textContent = isConfirm
        ? "お支払い方法につきましては、決済用のご案内メールをご確認ください。"
        : "ご回答ありがとうございました。またのご利用をお待ちしております。";
      this.elements.complete.hidden = false;
    }

    showStatusMessage(title, message) {
      this.elements.completeTitle.textContent = title;
      this.elements.completeMessage.textContent = message;
      this.elements.complete.hidden = false;
    }

    setIntroHtml(html) {
      if (this.elements.intro) {
        this.elements.intro.innerHTML = html;
      }
    }

    setText(elements, value) {
      elements.forEach((element) => {
        element.textContent = value;
      });
    }

    escapeHtml(value) {
      return value.replace(/[&<>"']/g, (character) => {
        return {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#039;"
        }[character];
      });
    }
  }

  customElements.define("purchase-intent", PurchaseIntent);
})();
